import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { DataTableFacetedFilter } from "@/shared/components/custom/data-table/data-table-faceted-filter";
import {
  Search,
  X,
  CircleQuestionMark,
  Circle,
  Clock,
  CircleCheck,
  CircleX,
  Plus,
  Users,
  Columns3,
  Check,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "@/core/redux/store";
import { useCallback, useState } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { SpecificationStatusType } from "@/shared/services/interface/types/ISpecificationFoldersTypes";
import {
  setSearch,
  setStatus,
  toggleColumnVisibility,
} from "@/core/redux/slices/supplier-responses-all";
import { RootState } from "@/core/redux/store";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/shared/components/ui/dropdown-menu";
import { headerConfig } from "../table/table-header/header-config";

export default function Header() {
  const dispatch = useDispatch();
  const { status, loading, hiddenColumnFields } = useSelector(
    (state: RootState) => state.supplierResponsesAll
  );
  const [localSearchValue, setLocalSearchValue] = useState("");
  const { t } = useTranslation();

  const handleToggleColumn = (field: string) => {
    dispatch(toggleColumnVisibility(field));
  };

  const isColumnVisible = (field: string) => {
    return !hiddenColumnFields.includes(field);
  };

  const searchWithDebounce = useCallback(
    debounce((value: string) => {
      dispatch(setSearch(value));
    }, 500),
    [dispatch]
  );

  const handleSearchChange = (value: string) => {
    const trimmedValue = value.trimStart();
    setLocalSearchValue(trimmedValue);
    searchWithDebounce(trimmedValue);
  };

  const handleStatusFilterChange = (value: string[]) => {
    dispatch(setStatus(value));
  };

  const isFiltered = status.length > 0 || localSearchValue.length > 0;
  const onResetFilters = () => {
    dispatch(setStatus([]));
    dispatch(setSearch(""));
    setLocalSearchValue("");
  };
  return (
    <div className="flex items-center justify-between my-[16px]">
      <div className="flex flex-1 items-center space-x-2 w-[350px]">
        <div className="relative mt-[-2px]">
          <div className="absolute left-2.5 top-1/2  -translate-y-1/2">
            {loading ? (
              <DotLottieReact
                src="/animations/lottie/loading-spinner.json"
                style={{
                  width: "35px",
                  height: "35px",
                  marginLeft: "-12px",
                }}
                autoplay={true}
                loop={true}
              />
            ) : (
              <Search width={16} height={16} className="mt-[-3px] ml-[-2px]" />
            )}
          </div>
          <Input
            placeholder={t("search")}
            value={localSearchValue}
            onChange={(event) => handleSearchChange(event.target.value)}
            className="h-[36px] w-[263px] pl-[25px]"
          />
        </div>
        <div className="max-w-max">
          <DataTableFacetedFilter
            value={status}
            onChange={handleStatusFilterChange}
            title={t("status")}
            options={status_options}
            multiple
          />
        </div>
        {isFiltered && (
          <Button
            variant="ghost"
            onClick={onResetFilters}
            className="h-8 px-2 lg:px-3 max-w-max"
          >
            {t("reset")}
            <X />
          </Button>
        )}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex border border-border items-center justify-center h-[36px] w-[36px] mr-[16px] rounded-md hover:bg-accent transition-colors">
            <Columns3 width={16} height={16} color="#18181B" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[200px]">
          <div className="p-1">
            {headerConfig.map((config, index) => {
              const isVisible = isColumnVisible(config.field);
              const canToggle = config.toggleable;

              return (
                <div key={config.field}>
                  <DropdownMenuItem
                    className={`relative pl-8 pr-2 py-1.5 ${
                      !canToggle
                        ? "opacity-50 cursor-default"
                        : "cursor-pointer"
                    }`}
                    onSelect={(e) => {
                      if (canToggle) {
                        handleToggleColumn(config.field);
                      }
                      e.preventDefault(); // Prevent dropdown from closing
                    }}
                  >
                    {isVisible && (
                      <Check className="absolute left-2 top-1/2 -translate-y-1/2 w-[10.667px] h-[10.667px]" />
                    )}
                    <span className="text-sm font-500">{config.label}</span>
                  </DropdownMenuItem>
                </div>
              );
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        className="max-w-max !gap-0 px-[12px] h-[36px] font-500"
        onClick={() => {}}
      >
        <Users className="mr-[4px]" />
        Yanıt topla
      </Button>
    </div>
  );
}

const status_options = [
  {
    value: "0",
    label: "Cevaplamayan",
    icon: CircleQuestionMark,
  },
  {
    value: "1",
    label: "Tamamlanan",
    icon: Circle,
  },
  {
    value: "2",
    label: "Son hareket",
    icon: Clock,
  },
  {
    value: "3",
    label: "Yorum",
    icon: CircleCheck,
  },
  {
    value: "4",
    label: "Davet durumu",
    icon: CircleX,
  },
];
